/**
 * SeatsTable — Mode A canonical V2 table per the `table-style` skill.
 * Columns: Title, Teams, Holders, Actions. Always-visible filter row,
 * sortable headers, Copy / CSV / Refresh / Columns toolbar, row click → panel.
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Copy, Download, Settings2, RefreshCw, FilterX,
  ArrowUp, ArrowDown, ArrowUpDown, ChevronRight, ArrowUpRight, User as UserIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuCheckboxItem,
  DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { OwnerAvatar } from '@/shared/components/OwnerAvatar';
import { useQueryClient } from '@tanstack/react-query';
import { useSeats, seatKeys } from '../api/seats';
import type { Seat } from '../types';

interface ColumnDef {
  key: string;
  label: string;
  sortKey?: string;
  filterType?: 'text' | 'select';
  /** For 'select' filters: discrete options shown in the dropdown. */
  filterOptions?: { label: string; value: string }[];
  getValue: (s: Seat) => string;
  /** For 'select' filters where the cell holds multiple values: list of values
   *  to match against the selected set (any-overlap = match). */
  filterValues?: (s: Seat) => string[];
  render?: (s: Seat) => React.ReactNode;
  align?: 'left' | 'right';
  noFilter?: boolean;
}

const TEAM_COLORS: Record<string, string> = {
  Leadership: 'bg-purple-100 text-purple-900',
  Sales: 'bg-blue-100 text-blue-900',
  Operations: 'bg-orange-100 text-orange-900',
  RnD: 'bg-green-100 text-green-900',
  Finances: 'bg-yellow-100 text-yellow-900',
  System: 'bg-gray-100 text-gray-900',
};

const VISIBILITY_KEY = 'atek-seats-column-visibility';
const FILTERS_KEY = 'atek-seats-column-filters';

function holderName(h: Seat['holders'][number]): string {
  return [h.firstname, h.lastname].filter(Boolean).join(' ') || h.email || 'Unknown';
}

interface Props {
  onRowClick: (seat: Seat) => void;
}

export function SeatsTable({ onRowClick }: Props) {
  const qc = useQueryClient();
  const { data, isLoading, isFetching } = useSeats();
  const seats = data?.seats ?? [];

  // Map seat id → title for the "Reports to" column.
  const parentTitle = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of seats) m.set(s.id, s.title);
    return (parentId: string | null) => (parentId ? m.get(parentId) ?? '—' : '');
  }, [seats]);

  // Options derived from the dataset for select filters
  const reportsToOptions = useMemo(() => {
    const opts = seats
      .filter((s) => s.parent_seat_id)
      .map((s) => parentTitle(s.parent_seat_id))
      .filter((v, i, a) => v && a.indexOf(v) === i)
      .sort()
      .map((label) => ({ label, value: label }));
    return [{ label: '— Root seats —', value: '__root__' }, ...opts];
  }, [seats, parentTitle]);

  const teamsOptions = useMemo(() => {
    const set = new Set<string>();
    for (const s of seats) for (const t of s.teams) set.add(t.name);
    return Array.from(set).sort().map((name) => ({ label: name, value: name }));
  }, [seats]);

  const holderOptions = useMemo(() => {
    const set = new Set<string>();
    for (const s of seats) for (const h of s.holders) set.add(holderName(h));
    return Array.from(set).sort().map((n) => ({ label: n, value: n }));
  }, [seats]);

  const COLUMNS: ColumnDef[] = useMemo(() => [
    { key: 'title', label: 'Seat', sortKey: 'title', filterType: 'text',
      getValue: (s) => s.title,
      render: (s) => <span className="font-medium">{s.title}</span> },
    { key: 'reports_to', label: 'Reports to', sortKey: 'reports_to',
      filterType: 'select', filterOptions: reportsToOptions,
      getValue: (s) => parentTitle(s.parent_seat_id),
      filterValues: (s) => [s.parent_seat_id ? parentTitle(s.parent_seat_id) : '__root__'],
      render: (s) => {
        const t = parentTitle(s.parent_seat_id);
        if (!t) return <span className="text-xs text-muted-foreground italic">root</span>;
        return (
          <span className="inline-flex items-center gap-1 text-sm">
            <ArrowUpRight className="h-3 w-3 text-muted-foreground shrink-0" />
            {t}
          </span>
        );
      } },
    { key: 'teams', label: 'Teams',
      filterType: 'select', filterOptions: teamsOptions,
      getValue: (s) => s.teams.map((t) => t.name).join(', '),
      filterValues: (s) => s.teams.map((t) => t.name),
      render: (s) => s.teams.length === 0
        ? <span className="text-xs text-muted-foreground">—</span>
        : (
          <div className="flex flex-wrap gap-1">
            {s.teams.map((t) => (
              <Badge key={t.id} variant="secondary" className={cn('text-xs', TEAM_COLORS[t.name] ?? '')}>
                {t.name}
              </Badge>
            ))}
          </div>
        ) },
    { key: 'holders', label: 'Holders',
      filterType: 'select', filterOptions: holderOptions,
      getValue: (s) => s.holders.map(holderName).join(', '),
      filterValues: (s) => s.holders.map(holderName),
      render: (s) => {
        if (s.holders.length === 0) {
          return (
            <Badge variant="outline" className="bg-zinc-50 text-zinc-500 text-[10px]">
              <UserIcon className="h-3 w-3 mr-1" /> Vacant
            </Badge>
          );
        }
        return (
          <div className="flex items-center gap-2">
            <TooltipProvider delayDuration={200}>
              <div className="flex -space-x-2">
                {s.holders.slice(0, 5).map((h) => (
                  <Tooltip key={h.id}>
                    <TooltipTrigger asChild>
                      <div className={cn('rounded-full ring-2 ring-background', h.is_primary && 'ring-amber-400')}>
                        <OwnerAvatar
                          ownerId={h.user_id}
                          photoUrl={h.profile_photo_url}
                          name={holderName(h)}
                          className="h-7 w-7"
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs">
                      {holderName(h)}
                      {h.is_primary && ' (Primary)'}
                      {h.territory && ` · ${h.territory}`}
                    </TooltipContent>
                  </Tooltip>
                ))}
                {s.holders.length > 5 && (
                  <div className="h-7 w-7 rounded-full bg-muted ring-2 ring-background flex items-center justify-center text-[10px] font-medium text-muted-foreground">
                    +{s.holders.length - 5}
                  </div>
                )}
              </div>
              <span className="text-xs text-muted-foreground tabular-nums">{s.holders.length}</span>
            </TooltipProvider>
          </div>
        );
      } },
    { key: 'actions', label: '', noFilter: true, getValue: () => '',
      render: () => <ChevronRight className="h-4 w-4 text-muted-foreground" />,
      align: 'right' },
  ], [parentTitle, reportsToOptions, teamsOptions, holderOptions]);

  // Column visibility
  const defaultVisibility = useMemo<Record<string, boolean>>(() => {
    const d: Record<string, boolean> = {};
    for (const c of COLUMNS) d[c.key] = true;
    return d;
  }, [COLUMNS]);

  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => {
    try {
      const stored = localStorage.getItem(VISIBILITY_KEY);
      return stored ? { ...defaultVisibility, ...JSON.parse(stored) } : defaultVisibility;
    } catch { return defaultVisibility; }
  });
  useEffect(() => { try { localStorage.setItem(VISIBILITY_KEY, JSON.stringify(columnVisibility)); } catch { /* storage is best-effort; ignore (private mode / quota) */ } }, [columnVisibility]);
  const isVisible = (k: string) => columnVisibility[k] !== false;
  const toggleColumn = (k: string) => setColumnVisibility((v) => ({ ...v, [k]: !isVisible(k) }));
  const visibleColumns = COLUMNS.filter((c) => isVisible(c.key));

  // Column filters
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(() => {
    try { return JSON.parse(localStorage.getItem(FILTERS_KEY) || '{}'); } catch { return {}; }
  });
  useEffect(() => { try { localStorage.setItem(FILTERS_KEY, JSON.stringify(columnFilters)); } catch { /* storage is best-effort; ignore (private mode / quota) */ } }, [columnFilters]);
  const setColumnFilter = (k: string, v: string) => setColumnFilters((p) => ({ ...p, [k]: v }));
  const clearFilters = () => setColumnFilters({});
  const hasActiveFilters = Object.values(columnFilters).some(Boolean);

  // Sort
  const [sortKey, setSortKey] = useState<string>('title');
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);
  const handleSort = (k: string) => {
    if (k === sortKey) setSortDirection((d) => (d === 1 ? -1 : 1));
    else { setSortKey(k); setSortDirection(1); }
  };

  // Filter + sort
  const filtered = useMemo(() => {
    const active = Object.entries(columnFilters).filter(([, v]) => v);
    if (active.length === 0) return seats;
    return seats.filter((seat) =>
      active.every(([key, value]) => {
        const col = COLUMNS.find((c) => c.key === key);
        if (!col || col.noFilter) return true;
        // Multi-select: any-overlap match between selected and cell values.
        if (col.filterType === 'select') {
          let selected: string[] = [];
          try { selected = JSON.parse(value) as string[]; } catch { return true; }
          if (selected.length === 0) return true;
          const values = col.filterValues ? col.filterValues(seat) : [col.getValue(seat)];
          return values.some((v) => selected.includes(v));
        }
        return col.getValue(seat).toLowerCase().includes(value.toLowerCase());
      }),
    );
  }, [seats, columnFilters, COLUMNS]);

  const sorted = useMemo(() => {
    const col = COLUMNS.find((c) => c.sortKey === sortKey);
    if (!col) return filtered;
    return [...filtered].sort((a, b) => sortDirection * col.getValue(a).localeCompare(col.getValue(b)));
  }, [filtered, sortKey, sortDirection, COLUMNS]);

  // Export
  function escapeCSV(v: unknown): string {
    if (v == null) return '';
    let s = String(v);
    if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
    if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
    return s;
  }
  const exportCols = COLUMNS.filter((c) => !c.noFilter);
  const handleCopy = () => {
    const header = exportCols.map((c) => c.label).join('\t');
    const body = sorted.map((s) => exportCols.map((c) => c.getValue(s)).join('\t'));
    navigator.clipboard.writeText([header, ...body].join('\n'));
    toast.success('Copied to clipboard');
  };
  const handleCSV = () => {
    const header = exportCols.map((c) => escapeCSV(c.label)).join(',');
    const body = sorted.map((s) => exportCols.map((c) => escapeCSV(c.getValue(s))).join(','));
    const blob = new Blob([[header, ...body].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `seats_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${sorted.length} rows`);
  };
  const handleRefresh = () => qc.invalidateQueries({ queryKey: seatKeys.all });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-sm text-muted-foreground">
          {filtered.length < seats.length
            ? `Showing ${filtered.length} of ${seats.length}`
            : `${seats.length} seat${seats.length !== 1 ? 's' : ''}`}
        </span>
        <div className="flex items-center gap-2 flex-wrap">
          {hasActiveFilters && (
            <Button variant="outline" size="sm" onClick={clearFilters}>
              <FilterX className="h-4 w-4 mr-1" /> Clear filters
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleCopy}>
            <Copy className="h-4 w-4 mr-1" /> Copy
          </Button>
          <Button variant="outline" size="sm" onClick={handleCSV}>
            <Download className="h-4 w-4 mr-1" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isFetching}>
            <RefreshCw className={cn('h-4 w-4 mr-1', isFetching && 'animate-spin')} /> Refresh
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings2 className="h-4 w-4 mr-1" /> Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {COLUMNS.filter((c) => !c.noFilter).map((c) => (
                <DropdownMenuCheckboxItem
                  key={c.key} checked={isVisible(c.key)} onCheckedChange={() => toggleColumn(c.key)}
                >
                  {c.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                {visibleColumns.map((col) => (
                  <TableHead key={`f-${col.key}`} className="p-1">
                    {col.noFilter ? null : col.filterType === 'select' ? (
                      <SelectFilter
                        colKey={col.key}
                        options={col.filterOptions ?? []}
                        value={columnFilters[col.key] || ''}
                        onChange={setColumnFilter}
                      />
                    ) : (
                      <Input
                        className="h-7 text-xs"
                        value={columnFilters[col.key] || ''}
                        onChange={(e) => setColumnFilter(col.key, e.target.value)}
                        placeholder="…"
                      />
                    )}
                  </TableHead>
                ))}
              </TableRow>
              <TableRow>
                {visibleColumns.map((col) => (
                  <TableHead key={col.key} className={cn(col.align === 'right' && 'text-right')}>
                    {col.sortKey ? (
                      <Button
                        variant="ghost" size="sm"
                        className="-ml-3 h-8 hover:bg-transparent"
                        onClick={() => handleSort(col.sortKey as string)}
                      >
                        {col.label}
                        {sortKey === col.sortKey
                          ? (sortDirection === 1
                            ? <ArrowUp className="ml-1 h-3 w-3" />
                            : <ArrowDown className="ml-1 h-3 w-3" />)
                          : <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />}
                      </Button>
                    ) : col.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={`s-${i}`}>
                    {visibleColumns.map((c) => (
                      <TableCell key={c.key}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : sorted.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} className="py-12 text-center text-sm text-muted-foreground">
                    No seats match these filters.
                  </TableCell>
                </TableRow>
              ) : (
                sorted.map((seat) => (
                  <TableRow
                    key={seat.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => onRowClick(seat)}
                  >
                    {visibleColumns.map((c) => (
                      <TableCell
                        key={c.key}
                        className={cn('text-sm', c.align === 'right' && 'text-right tabular-nums')}
                      >
                        {c.render ? c.render(seat) : (c.getValue(seat) || '—')}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

export default SeatsTable;

// =============================================================================
// SelectFilter — multi-select dropdown for the filter row.
// Selected values stored as JSON-encoded string[] in columnFilters.
// =============================================================================

function SelectFilter({
  colKey, options, value, onChange,
}: {
  colKey: string;
  options: { label: string; value: string }[];
  value: string;
  onChange: (key: string, value: string) => void;
}) {
  const selected: string[] = useMemo(() => {
    try { return value ? JSON.parse(value) : []; } catch { return []; }
  }, [value]);

  const toggle = (opt: string) => {
    const next = selected.includes(opt)
      ? selected.filter((o) => o !== opt)
      : [...selected, opt];
    onChange(colKey, next.length ? JSON.stringify(next) : '');
  };

  const label = selected.length === 0
    ? '—'
    : selected.length === 1
      ? (options.find((o) => o.value === selected[0])?.label ?? '1 selected')
      : `${selected.length} selected`;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 w-full text-xs justify-between font-normal"
        >
          <span className="truncate">{label}</span>
          {selected.length > 0 && (
            <FilterX
              className="h-3 w-3 opacity-50 hover:opacity-100 shrink-0 ml-1"
              onClick={(e) => { e.stopPropagation(); onChange(colKey, ''); }}
            />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
        {options.length === 0 ? (
          <div className="p-2 text-xs text-muted-foreground">No options</div>
        ) : options.map((opt) => (
          <DropdownMenuCheckboxItem
            key={opt.value}
            checked={selected.includes(opt.value)}
            onCheckedChange={() => toggle(opt.value)}
            onSelect={(e) => e.preventDefault()}
          >
            {opt.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
